<template lang="pug">
span
  v-menu(v-model="showMenu" rounded="lg" allow-overflow attach :close-on-content-click="false" offset-y max-height="500" min-width="320" max-width="640" nudge-bottom="8")
    template(v-slot:activator="{ on, attrs }")
      v-btn.px-2.button-text-normal.button-filter.text-none(v-if="selectedOption" outlined rounded height="28" v-bind="attrs" v-on="on")
        v-icon.mr-1(@click.stop="clear" size=13) $yzi-x-circle
        .text-xs.font-semibold Company
        .px-1.neutral400--text | 
        .text-xs.primary--text.font-medium {{ displayText }}
        v-icon.ml-1(size=16) $yzi-chevron-down
      v-btn.px-2.button-text-normal.border-dashed.button-filter(v-else outlined rounded height="28" v-bind="attrs" v-on="on")
        v-icon.mr-1(size=13) $yzi-plus-circle
        .text-xs.text-none.font-semibold Company

    .white
      .pa-4
        .font-semibold.mb-3.text-sm {{ $t('filter_by') }} company
        v-skeleton-loader(v-if="isLoading" type="list-item-three-line" width="200")
        div(v-else)
          // Search input - sticky
          .sticky-search.white.pb-2
            v-text-field.round-xl(v-model="searchQuery" placeholder="Search company..." hide-details dense outlined clearable)

          .scrollable-content(style="max-height: 300px; overflow-y: auto;")
            // Select All option
            .hover-bg(v-if="!searchQuery" @click="toggleSelectAll")
              .d-flex.align-center.justify-space-between.px-2.py-2
                .text-sm.font-weight-medium All companies
                v-checkbox(:input-value="isAllSelected" :indeterminate="isIndeterminate" hide-details dense @click.stop="toggleSelectAll")
              v-divider.basic300

            // Individual company options
            template(v-if="filteredCompanies.length")
              .hover-bg(v-for="company in filteredCompanies" :key="company.id" @click="toggleCompany(company)")
                .d-flex.align-center.justify-space-between.px-2.py-2
                  .text-sm {{ company.name }}
                  v-checkbox(:input-value="isCompanySelected(company)" hide-details dense @click.stop="toggleCompany(company)")
                v-divider.basic300
            template(v-else)
              .px-2.py-2.text-sm.neutral500--text No company found

      .bottom-0.sticky.z-1.white
        .d-flex.align-center.px-4.py-3.border-t
          .text-sm.font-semibold.underline.normal-case.neutral700--text.cursor-pointer(v-if="selectedCompanies.length > 0" @click="clear") {{ $t('clear') }}
          v-spacer
          v-btn.button-sm(@click="apply" color="primary" depressed :disabled="selectedCompanies.length === 0") {{ $t('apply') }}

</template>

<script>
import {mapGetters} from "vuex";

export default {
  props: {
    filter: {
      type: Object,
      default: (() => {
      })
    },
    checkInitialValue: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    showMenu: false,
    key: 'companies',
    isLoading: true,
    companies: [],
    selectedCompanies: [],
    selectedOption: null,
    searchQuery: ''
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    displayText() {
      if (this.selectedCompanies.length === 0) {
        return 'All companies'
      } else if (this.selectedCompanies.length === this.companies.length) {
        return 'All companies'
      } else if (this.selectedCompanies.length === 1) {
        return this.selectedCompanies[0].name
      } else {
        return `${this.selectedCompanies.length} companies selected`
      }
    },
    isAllSelected() {
      return this.companies.length > 0 && this.selectedCompanies.length === this.companies.length
    },
    isIndeterminate() {
      return this.selectedCompanies.length > 0 && this.selectedCompanies.length < this.companies.length
    },
    filteredCompanies() {
      if (!this.searchQuery || this.searchQuery.trim() === '') {
        return this.companies
      }
      const query = this.searchQuery.toLowerCase().trim()
      return this.companies.filter(company =>
          company.name.toLowerCase().includes(query)
      )
    }
  },
  watch: {
    filter: {
      handler() {
        if (!this.filter.key) {
          this.clearData()
        }
      }
    },
    showMenu(newValue, oldValue) {
      // When menu is closed, clear search query
      if (!newValue && oldValue) {
        this.searchQuery = ''
      }
    }
  },
  mounted() {
    this.fetchCompanies()
    this.setData()
  },
  methods: {
    fetchCompanies() {
      this.isLoading = true

      const params = {
        hq: this.company.id,
        fields: 'id,name',
        page_size: -1
      }
      this.$axios.get('/companies', {
        params
      })
          .then(resp => {
            if (!resp.data?.results) {
              this.companies = []
              this.isLoading = false
              return
            }

            this.companies = resp.data.results.map(item => {
              return {
                name: item.name,
                id: item.id
              }
            })

            this.isLoading = false
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
    },
    clear() {
      this.clearData()
      this.$emit('update:filter', {})
    },
    clearData() {
      this.selectedOption = null
      this.selectedCompanies = []
    },
    apply() {
      this.showMenu = false
      this.selectedOption = [...this.selectedCompanies]
      const filter = {key: this.key, value: this.selectedCompanies.map(company => company.id).join(',')}
      this.$emit('update:filter', filter)
    },
    isCompanySelected(company) {
      return this.selectedCompanies.some(selected => selected.id === company.id)
    },
    toggleCompany(company) {
      const index = this.selectedCompanies.findIndex(selected => selected.id === company.id)
      if (index > -1) {
        this.selectedCompanies.splice(index, 1)
      } else {
        this.selectedCompanies.push(company)
      }
    },
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedCompanies = []
      } else {
        this.selectedCompanies = [...this.companies]
      }
    },
    setData() {
      if (this.checkInitialValue) {
        if (this.filter.key === this.key && this.filter.value) {
          const companyIds = this.filter.value.split(',').map(id => parseInt(id.trim()))

          // Find matching companies from the fetched list
          const matchingCompanies = this.companies.filter(company =>
            companyIds.includes(company.id)
          )

          if (matchingCompanies.length > 0) {
            this.selectedCompanies = matchingCompanies
            this.selectedOption = [...matchingCompanies]
          }
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.button-filter {
  min-width: 0 !important;
  color: #848484;
}

::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  }

  .error--text {
    color: var(--v-danger500) !important;

    fieldset {
      border-color: var(--v-danger500);
    }
  }
}

.sticky-search {
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
  position: sticky;
  top: 0;
  z-index: 1;
}
</style>
